import { ComparatorValue } from "@/components/table/AdvancedSearchFormRow";
import { SortDirection } from "@tanstack/react-table";

export type BaseFieldSearchConfig = {
  label?: string;
  key: string;
  freeSearch?: boolean;
  additionalQuery?: object[];
};

export type StringFieldSearchConfig = {
  type: "string";
};

export type BooleanFieldSearchConfig = {
  type: "boolean";
};

export type NumberFieldSearchConfig = {
  type: "number";
  multiplyBy?: number;
};

export type CurrencyFieldSearchConfig = {
  type: "currency";
};

export type EnumFieldSearchConfig = {
  type: "enum";
  useBooleans?: boolean; // if the database field are booleans (legacy data schema)
  options: { name: string; label?: string }[];
};

export type MultilingualFieldSearchConfig = {
  type: "multilingual";
};

export type DimensionsFieldSearchConfig = {
  type: "dimensions";
};

export type FieldConfigTypes =
  | StringFieldSearchConfig
  | BooleanFieldSearchConfig
  | EnumFieldSearchConfig
  | NumberFieldSearchConfig
  | CurrencyFieldSearchConfig
  | MultilingualFieldSearchConfig
  | DimensionsFieldSearchConfig;

export type AdvancedSearchFieldConfig<
  FieldsTypes extends FieldConfigTypes = FieldConfigTypes,
> = BaseFieldSearchConfig & FieldsTypes;

export type AdvancedSearchGroupConfig = {
  type: "group";
  label: string;
  fields: AdvancedSearchFieldConfig[];
};

export type AdvancedSearchConfig = (
  | AdvancedSearchGroupConfig
  | AdvancedSearchFieldConfig
)[];

export function isBooleanFieldConfig(
  field: AdvancedSearchFieldConfig,
): field is AdvancedSearchFieldConfig<BooleanFieldSearchConfig> {
  return field.type === "boolean";
}

export function isStringFieldConfig(
  field: AdvancedSearchFieldConfig,
): field is AdvancedSearchFieldConfig<StringFieldSearchConfig> {
  return field.type === "string";
}

export function isEnumFieldConfig(
  field: AdvancedSearchFieldConfig,
): field is AdvancedSearchFieldConfig<EnumFieldSearchConfig> {
  return field.type === "enum";
}

export function isNumberFieldConfig(
  field: AdvancedSearchFieldConfig,
): field is AdvancedSearchFieldConfig<NumberFieldSearchConfig> {
  return field.type === "number";
}

export function isCurrencyFieldConfig(
  field: AdvancedSearchFieldConfig,
): field is AdvancedSearchFieldConfig<CurrencyFieldSearchConfig> {
  return field.type === "currency";
}

export function isMultilingualFieldConfig(
  field: AdvancedSearchFieldConfig,
): field is AdvancedSearchFieldConfig<MultilingualFieldSearchConfig> {
  return field.type === "multilingual";
}

export function findFieldByKey(
  key: string,
  config: AdvancedSearchConfig,
): AdvancedSearchFieldConfig | undefined {
  for (const c of config) {
    if (c.type == "group") {
      const foundField = c.fields.find((f) => f.key === key);
      if (foundField != undefined) return foundField;
      else continue;
    }
    if (c.key === key) return c;
  }
  return undefined;
}

export const numberFieldComparator = [
  "equals",
  "notEquals",
  "gt",
  "lt",
  "gte",
  "lte",
] as const;

type NumberField = NumberFieldSearchConfig & {
  comparator?: (typeof numberFieldComparator)[number];
  value?: number;
};

type CurrencyField = CurrencyFieldSearchConfig & {
  comparator?: (typeof numberFieldComparator)[number];
  value?: number;
};

export const booleanFieldComparator = ["equals", "notEquals"];

type BooleanField = BooleanFieldSearchConfig & {
  comparator: (typeof booleanFieldComparator)[number];
  value: "true" | "false";
};

type EnumField = EnumFieldSearchConfig & {
  comparator?: (typeof booleanFieldComparator)[number];
  value?: string;
};

type UnknownField = {
  type?: "unknown";
};

type NotUnknownField = Omit<BaseFieldSearchConfig, "label">;

export const stringFieldComparator = [
  { value: "equals", label: "advancedSearchStringEqualComparator" } as const,
  "notEquals",
  "startsWith",
  "endsWith",
  "contains",
  "notContains",
  "empty",
  "notEmpty",
] as const;

export type StringField = StringFieldSearchConfig & {
  comparator?: ComparatorValue<(typeof stringFieldComparator)[number]>;
  value?: string;
};

export const jsonFieldComparator = ["contains"] as const;

export type MultilingualField = MultilingualFieldSearchConfig & {
  comparator?: ComparatorValue<(typeof jsonFieldComparator)[number]>;
  value?: string;
};

export type FormFieldTypes =
  | EnumField
  | NumberField
  | StringField
  | BooleanField
  | CurrencyField
  | MultilingualField;

export type AdvancedSearchKnownField<
  FieldType extends FormFieldTypes = FormFieldTypes,
> = NotUnknownField & FieldType;

export type AdvancedSearchFormField<
  FieldType extends FormFieldTypes = FormFieldTypes,
> = AdvancedSearchKnownField<FieldType> | UnknownField;

export type SubmittableSearchFormField<
  FieldType extends FormFieldTypes = FormFieldTypes,
> = AdvancedSearchKnownField<FieldType> &
  Pick<Required<AdvancedSearchKnownField<FieldType>>, "comparator">;

export type SortField = { key?: string; sortMode: SortDirection | false };

export type SubmitableSortField = { key: string; sortMode: SortDirection };

export function isUnknownFormField(
  field: AdvancedSearchFormField,
): field is UnknownField {
  return field.type == undefined || field.type === "unknown";
}

export function isKnownFormField(
  field: AdvancedSearchFormField,
): field is AdvancedSearchKnownField {
  return field.type !== "unknown";
}

export function isSubmittableFormField(
  field: AdvancedSearchKnownField,
): field is SubmittableSearchFormField {
  return field.comparator != undefined;
}

export function isSubmittableSortField(
  field: SortField,
): field is SubmitableSortField {
  return (
    field.key != undefined && field.key.length > 0 && field.sortMode != false
  );
}

export const isFromForeignTable = (fieldConfig: AdvancedSearchFieldConfig) =>
  fieldConfig.key.includes(".");

export const isFromThisTable = (fieldConfig: AdvancedSearchFieldConfig) =>
  !isFromForeignTable(fieldConfig);

export function advancedSearchToSubmittable(
  query: AdvancedSearchFormField[],
): SubmittableSearchFormField[] {
  return query.filter(isKnownFormField).filter(isSubmittableFormField);
}

/**
 * Extract the items from he config groups
 * @param config
 * @returns
 */
export const flattenAdvancedSearchConfig = (
  config: AdvancedSearchConfig,
): AdvancedSearchFieldConfig[] =>
  config.flatMap((c) => (c.type === "group" ? c.fields : c));

export const filterOutForeignFields = (config: AdvancedSearchConfig) =>
  config
    .map((item) => {
      if (item.type === "group") {
        return { ...item, fields: item.fields.filter(isFromThisTable) };
      }
      return item;
    })
    .filter((item) => {
      if (item.type === "group") return item.fields.length > 0;
      return isFromThisTable(item);
    });

export type SearchOperator = "and" | "or";

export function extractLabel(fieldConfig: AdvancedSearchFieldConfig) {
  if (fieldConfig.label) return fieldConfig.label;
  if (!fieldConfig.key.includes(".")) return fieldConfig.key;

  const splittedConfig = fieldConfig.key.split(".");
  return splittedConfig[splittedConfig.length - 1];
}
